import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Video, VideoOff, Mic, MicOff, Phone, Users, Monitor, MessageSquare, Settings, ArrowLeft, Loader2, Clock, Calendar, Circle, ClipboardList, PenTool, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import { getUser } from '@/utils/auth';

const LiveSessionPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [joinInfo, setJoinInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [isInMeeting, setIsInMeeting] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const user = getUser();

  useEffect(() => {
    fetchSessionInfo();
    return () => {
      // Record attendance leave when component unmounts
      if (isInMeeting) {
        recordAttendanceLeave();
      }
      // Cleanup Jitsi when component unmounts
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
      }
    };
  }, [sessionId]);

  const fetchSessionInfo = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/live-sessions/${sessionId}`);
      setSessionInfo(response.data);
      setIsRecording(response.data.recording_status === 'recording');
    } catch (error) {
      console.error('Failed to fetch session:', error);
      toast.error('Session not found or access denied');
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  // Attendance tracking functions
  const recordAttendanceJoin = async () => {
    try {
      await api.post(`/live-sessions/${sessionId}/attendance/join`);
    } catch (error) {
      console.error('Failed to record attendance join:', error);
    }
  };

  const recordAttendanceLeave = async () => {
    try {
      await api.post(`/live-sessions/${sessionId}/attendance/leave`);
    } catch (error) {
      console.error('Failed to record attendance leave:', error);
    }
  };

  const fetchAttendance = async () => {
    try {
      const response = await api.get(`/live-sessions/${sessionId}/attendance`);
      setAttendanceData(response.data);
      setShowAttendance(true);
    } catch (error) {
      toast.error('Failed to fetch attendance');
    }
  };

  // Recording functions
  const startRecording = async () => {
    try {
      await api.post(`/live-sessions/${sessionId}/recording/start`);
      setIsRecording(true);
      toast.success('Recording started');
      // Also trigger Jitsi's built-in recording if available
      if (jitsiApiRef.current) {
        jitsiApiRef.current.executeCommand('startRecording', {
          mode: 'file'
        });
      }
    } catch (error) {
      toast.error('Failed to start recording');
    }
  };

  const stopRecording = async () => {
    try {
      await api.post(`/live-sessions/${sessionId}/recording/stop`);
      setIsRecording(false);
      toast.success('Recording stopped');
      // Stop Jitsi's built-in recording
      if (jitsiApiRef.current) {
        jitsiApiRef.current.executeCommand('stopRecording', 'file');
      }
    } catch (error) {
      toast.error('Failed to stop recording');
    }
  };

  const joinSession = async () => {
    setIsJoining(true);
    try {
      const response = await api.post(`/live-sessions/${sessionId}/join`);
      setJoinInfo(response.data);
      
      // Record attendance when joining
      await recordAttendanceJoin();
      
      // Load Jitsi Meet External API
      await loadJitsiScript();
      initializeJitsi(response.data);
    } catch (error) {
      console.error('Failed to join session:', error);
      toast.error(error.response?.data?.detail || 'Failed to join session');
      setIsJoining(false);
    }
  };

  const loadJitsiScript = () => {
    return new Promise((resolve, reject) => {
      if (window.JitsiMeetExternalAPI) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://meet.jit.si/external_api.js';
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const initializeJitsi = (info) => {
    const domain = info.jitsi_domain;
    const options = {
      roomName: info.room_name,
      width: '100%',
      height: '100%',
      parentNode: jitsiContainerRef.current,
      userInfo: {
        displayName: info.display_name,
      },
      configOverwrite: {
        startWithAudioMuted: true,
        startWithVideoMuted: false,
        prejoinPageEnabled: false,
        disableDeepLinking: true,
        enableWelcomePage: false,
        enableClosePage: false,
        disableInviteFunctions: true,
        // Enable whiteboard for annotation
        whiteboard: {
          enabled: true,
          collabServerBaseUrl: 'https://excalidraw-backend.jitsi.net'
        },
        toolbarButtons: [
          'microphone',
          'camera',
          'desktop',
          'fullscreen',
          'fodeviceselection',
          'hangup',
          'chat',
          'recording',
          'settings',
          'raisehand',
          'videoquality',
          'tileview',
          'participants-pane',
          'whiteboard',  // Annotation/whiteboard tool
          'shareaudio',
          'select-background',
        ],
        // Branding
        TOOLBAR_ALWAYS_VISIBLE: true,
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        DEFAULT_BACKGROUND: '#163329',
        DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
        // Recording settings
        fileRecordingsEnabled: true,
        localRecording: {
          enabled: true,
          format: 'webm'
        },
      },
      interfaceConfigOverwrite: {
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        SHOW_BRAND_WATERMARK: false,
        BRAND_WATERMARK_LINK: '',
        SHOW_POWERED_BY: false,
        SHOW_PROMOTIONAL_CLOSE_PAGE: false,
        TOOLBAR_BUTTONS: [
          'microphone',
          'camera',
          'desktop',
          'fullscreen',
          'hangup',
          'chat',
          'recording',
          'settings',
          'raisehand',
          'tileview',
          'participants-pane',
          'whiteboard',
        ],
        SETTINGS_SECTIONS: ['devices', 'language'],
        MOBILE_APP_PROMO: false,
        HIDE_INVITE_MORE_HEADER: true,
        DISABLE_TRANSCRIPTION_SUBTITLES: true,
      },
    };

    // Set moderator privileges if teacher
    if (info.is_moderator) {
      options.configOverwrite.toolbarButtons.push('mute-everyone', 'mute-video-everyone');
    }

    const jitsiApi = new window.JitsiMeetExternalAPI(domain, options);
    jitsiApiRef.current = jitsiApi;

    // Event listeners
    jitsiApi.addListener('videoConferenceJoined', () => {
      setIsInMeeting(true);
      setIsJoining(false);
      toast.success('Joined the session!');
    });

    jitsiApi.addListener('videoConferenceLeft', () => {
      setIsInMeeting(false);
      handleLeaveSession();
    });

    jitsiApi.addListener('participantJoined', () => {
      setParticipantCount(prev => prev + 1);
    });

    jitsiApi.addListener('participantLeft', () => {
      setParticipantCount(prev => Math.max(0, prev - 1));
    });

    jitsiApi.addListener('readyToClose', () => {
      handleLeaveSession();
    });
  };

  const handleLeaveSession = async () => {
    // Record attendance leave
    await recordAttendanceLeave();
    
    if (jitsiApiRef.current) {
      jitsiApiRef.current.dispose();
      jitsiApiRef.current = null;
    }
    setIsInMeeting(false);
    setJoinInfo(null);
    navigate(-1);
  };

  const startSession = async () => {
    try {
      await api.post(`/live-sessions/${sessionId}/start`);
      toast.success('Session started! Students have been notified.');
      fetchSessionInfo();
      joinSession();
    } catch (error) {
      toast.error('Failed to start session');
    }
  };

  const endSession = async () => {
    if (!confirm('Are you sure you want to end this session for all participants?')) return;
    
    try {
      await api.post(`/live-sessions/${sessionId}/end`);
      toast.success('Session ended');
      handleLeaveSession();
    } catch (error) {
      toast.error('Failed to end session');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" style={{ color: '#163329' }} />
          <p style={{ color: '#5C635A' }}>Loading session...</p>
        </div>
      </div>
    );
  }

  // Show Jitsi meeting when in session
  if (isInMeeting || isJoining) {
    return (
      <div className="h-screen flex flex-col" style={{ backgroundColor: '#1a1a1a' }}>
        {/* Meeting Header */}
        <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: '#163329' }}>
          <div className="flex items-center gap-4">
            <h2 className="text-white font-semibold">{sessionInfo?.title}</h2>
            <Badge className="bg-red-500 text-white animate-pulse">
              <span className="w-2 h-2 rounded-full bg-white mr-2 inline-block"></span>
              LIVE
            </Badge>
          </div>
          <div className="flex items-center gap-4">
            {/* Recording indicator */}
            {isRecording && (
              <div className="flex items-center gap-2 text-red-400">
                <Circle className="h-3 w-3 fill-current animate-pulse" />
                <span className="text-sm">Recording</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-white/80">
              <Users className="h-4 w-4" />
              <span>{participantCount + 1}</span>
            </div>
            {user?.role === 'teacher' && sessionInfo?.teacher_id === user?.id && (
              <>
                {/* Recording Controls */}
                {!isRecording ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={startRecording}
                    className="rounded-full border-white/30 text-white hover:bg-white/10"
                  >
                    <Circle className="h-4 w-4 mr-2 text-red-500" />
                    Record
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={stopRecording}
                    className="rounded-full border-red-400 text-red-400 hover:bg-red-500/10"
                  >
                    <Circle className="h-4 w-4 mr-2 fill-current" />
                    Stop Recording
                  </Button>
                )}
                
                {/* Attendance Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchAttendance}
                  className="rounded-full border-white/30 text-white hover:bg-white/10"
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Attendance
                </Button>
                
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={endSession}
                  className="rounded-full"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  End Session
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Jitsi Container */}
        <div className="flex-1 relative">
          {isJoining && (
            <div className="absolute inset-0 flex items-center justify-center z-10" style={{ backgroundColor: '#1a1a1a' }}>
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-white" />
                <p className="text-white/80">Connecting to session...</p>
              </div>
            </div>
          )}
          <div ref={jitsiContainerRef} className="w-full h-full" />
        </div>
      </div>
    );
  }

  // Pre-join lobby
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 -ml-2"
          style={{ color: '#5C635A' }}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="overflow-hidden" style={{ borderColor: '#E3E1DB' }}>
            {/* Session Header */}
            <div className="p-6 text-white" style={{ backgroundColor: '#163329' }}>
              <div className="flex items-center gap-2 mb-4">
                <Video className="h-6 w-6" />
                <Badge 
                  className={`${
                    sessionInfo?.status === 'live' ? 'bg-red-500 animate-pulse' : 
                    sessionInfo?.status === 'ended' ? 'bg-gray-500' : 'bg-blue-500'
                  }`}
                >
                  {sessionInfo?.status === 'live' ? 'LIVE NOW' : 
                   sessionInfo?.status === 'ended' ? 'ENDED' : 'SCHEDULED'}
                </Badge>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Spectral, serif' }}>
                {sessionInfo?.title}
              </h1>
              <p className="text-white/80">{sessionInfo?.course_title}</p>
            </div>

            <CardContent className="p-6">
              {/* Session Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0EFEA' }}>
                    <Calendar className="h-5 w-5" style={{ color: '#163329' }} />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#5C635A' }}>Scheduled</p>
                    <p className="font-medium" style={{ color: '#163329' }}>
                      {formatDate(sessionInfo?.scheduled_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0EFEA' }}>
                    <Clock className="h-5 w-5" style={{ color: '#163329' }} />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#5C635A' }}>Duration</p>
                    <p className="font-medium" style={{ color: '#163329' }}>
                      {sessionInfo?.duration} minutes
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0EFEA' }}>
                    <Users className="h-5 w-5" style={{ color: '#163329' }} />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#5C635A' }}>Host</p>
                    <p className="font-medium" style={{ color: '#163329' }}>
                      {sessionInfo?.teacher_name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                {sessionInfo?.status === 'ended' ? (
                  <div className="text-center py-8">
                    <VideoOff className="h-12 w-12 mx-auto mb-4" style={{ color: '#E3E1DB' }} />
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#163329' }}>Session Ended</h3>
                    <p className="mb-4" style={{ color: '#5C635A' }}>This live session has concluded.</p>
                    
                    {/* Teacher can view attendance and recording for ended sessions */}
                    {user?.role === 'teacher' && sessionInfo?.teacher_id === user?.id && (
                      <div className="flex justify-center gap-3">
                        <Button
                          variant="outline"
                          onClick={fetchAttendance}
                          className="rounded-full"
                          style={{ borderColor: '#163329', color: '#163329' }}
                        >
                          <ClipboardList className="h-4 w-4 mr-2" />
                          View Attendance
                        </Button>
                        {sessionInfo?.recording_url && (
                          <Button
                            variant="outline"
                            onClick={() => window.open(sessionInfo.recording_url, '_blank')}
                            className="rounded-full"
                            style={{ borderColor: '#D86B47', color: '#D86B47' }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            View Recording
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ) : user?.role === 'teacher' && sessionInfo?.teacher_id === user?.id ? (
                  // Teacher controls
                  <div className="space-y-3">
                    {sessionInfo?.status === 'scheduled' ? (
                      <Button
                        onClick={startSession}
                        className="w-full rounded-full text-lg py-6"
                        style={{ backgroundColor: '#163329' }}
                        data-testid="start-session-btn"
                      >
                        <Video className="h-5 w-5 mr-2" />
                        Start Session
                      </Button>
                    ) : (
                      <Button
                        onClick={joinSession}
                        className="w-full rounded-full text-lg py-6"
                        style={{ backgroundColor: '#163329' }}
                        disabled={isJoining}
                        data-testid="join-session-btn"
                      >
                        {isJoining ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <Video className="h-5 w-5 mr-2" />
                            Rejoin Session
                          </>
                        )}
                      </Button>
                    )}
                    <p className="text-center text-sm" style={{ color: '#5C635A' }}>
                      Starting the session will notify all enrolled students
                    </p>
                  </div>
                ) : (
                  // Student controls
                  <div className="space-y-3">
                    {sessionInfo?.status === 'live' ? (
                      <Button
                        onClick={joinSession}
                        className="w-full rounded-full text-lg py-6"
                        style={{ backgroundColor: '#163329' }}
                        disabled={isJoining}
                        data-testid="join-session-btn"
                      >
                        {isJoining ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <Video className="h-5 w-5 mr-2" />
                            Join Live Session
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="h-12 w-12 mx-auto mb-4" style={{ color: '#E2A761' }} />
                        <h3 className="text-lg font-semibold mb-2" style={{ color: '#163329' }}>
                          Session Not Started Yet
                        </h3>
                        <p style={{ color: '#5C635A' }}>
                          Please wait for the teacher to start the session.
                          You'll receive a notification when it begins.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tips */}
              <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                <h4 className="font-semibold mb-2" style={{ color: '#163329' }}>Before Joining</h4>
                <ul className="text-sm space-y-1" style={{ color: '#5C635A' }}>
                  <li>• Ensure your camera and microphone are working</li>
                  <li>• Use headphones for better audio quality</li>
                  <li>• Find a quiet, well-lit space</li>
                  <li>• Close unnecessary browser tabs</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Attendance Dialog */}
      <Dialog open={showAttendance} onOpenChange={setShowAttendance}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              <ClipboardList className="inline h-5 w-5 mr-2" />
              Session Attendance
            </DialogTitle>
          </DialogHeader>
          {attendanceData && (
            <div className="space-y-6 pt-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4 text-center" style={{ borderColor: '#E3E1DB' }}>
                  <p className="text-2xl font-bold" style={{ color: '#163329' }}>
                    {attendanceData.unique_attendees}
                  </p>
                  <p className="text-sm" style={{ color: '#5C635A' }}>Attendees</p>
                </Card>
                <Card className="p-4 text-center" style={{ borderColor: '#E3E1DB' }}>
                  <p className="text-2xl font-bold" style={{ color: '#163329' }}>
                    {attendanceData.total_enrolled}
                  </p>
                  <p className="text-sm" style={{ color: '#5C635A' }}>Enrolled</p>
                </Card>
                <Card className="p-4 text-center" style={{ borderColor: '#E3E1DB' }}>
                  <p className="text-2xl font-bold" style={{ color: attendanceData.attendance_rate >= 70 ? '#2E7D32' : '#D86B47' }}>
                    {attendanceData.attendance_rate}%
                  </p>
                  <p className="text-sm" style={{ color: '#5C635A' }}>Attendance Rate</p>
                </Card>
              </div>

              {/* Attendance List */}
              <div>
                <h4 className="font-semibold mb-3" style={{ color: '#163329' }}>Attendance Records</h4>
                {attendanceData.records.length === 0 ? (
                  <p className="text-center py-8" style={{ color: '#5C635A' }}>
                    No attendance records yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {attendanceData.records.map((record, idx) => (
                      <div 
                        key={record.id || idx}
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: '#F0EFEA' }}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                            style={{ backgroundColor: record.user_role === 'teacher' ? '#D86B47' : '#163329' }}
                          >
                            {record.user_name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-medium text-sm" style={{ color: '#163329' }}>
                              {record.user_name}
                              {record.user_role === 'teacher' && (
                                <Badge className="ml-2 text-xs" style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>
                                  Teacher
                                </Badge>
                              )}
                            </p>
                            <p className="text-xs" style={{ color: '#5C635A' }}>{record.user_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm" style={{ color: '#163329' }}>
                            {new Date(record.join_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {record.leave_time && (
                              <span> - {new Date(record.leave_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            )}
                          </p>
                          {record.duration_minutes && (
                            <p className="text-xs" style={{ color: '#5C635A' }}>
                              {Math.round(record.duration_minutes)} min
                            </p>
                          )}
                          {!record.leave_time && (
                            <Badge className="text-xs bg-green-500 text-white">
                              Currently in session
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveSessionPage;
